import {
  CalculatorsRequest,
  DraftVesselType,
  PerformanceInputRow,
  PerformanceKind,
  SailingCondition,
} from "../models/calculators.models";

const DRAFT_VESSEL_TYPES: DraftVesselType[] = [
  "Capes (incl. Newcs)",
  "Handy",
  "Panamax (incl. Baby Capes)",
  "Supramax",
];
const SAILING_CONDITIONS: SailingCondition[] = ["Ballast", "Laden"];
const PERFORMANCE_KINDS: PerformanceKind[] = ["ballastEco", "ladenEco", "ballastFull", "ladenFull"];

const isPositiveNumber = (value: number | null): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const validatePerformanceRow = (row: PerformanceInputRow, errors: string[]) => {
  if (!PERFORMANCE_KINDS.includes(row.kind)) errors.push("Performance row kind is not supported.");

  const fields = [
    [row.expectedSpeed, `${row.kind} expected speed`],
    [row.expectedConsumption, `${row.kind} expected consumption`],
    [row.cpSpeed, `${row.kind} CP speed`],
    [row.cpConsumption, `${row.kind} CP consumption`],
  ] as const;

  fields.forEach(([value, label]) => {
    if (value !== null && !isPositiveNumber(value)) errors.push(`${label} must be blank or positive.`);
  });
};

// Keeps local mock and live requests on the same positive-number contract.
export const validateCalculatorsRequest = (request: CalculatorsRequest): string[] => {
  const errors: string[] = [];

  if (!DRAFT_VESSEL_TYPES.includes(request.draft.vesselType)) errors.push("Vessel type is not supported.");
  if (!SAILING_CONDITIONS.includes(request.draft.condition)) errors.push("Draft condition is not supported.");

  const draftFields = [
    [request.draft.speedExponent, "Draft speed exponent"],
    [request.draft.normalizationDraft, "Normalization draft"],
    [request.draft.stw, "STW"],
    [request.draft.meConsumption, "ME consumption"],
    [request.draft.aeConsumption, "AE consumption"],
    [request.draft.draft, "Actual draft"],
    [request.fuelCurve.speedExponent, "Fuel-curve speed exponent"],
    [request.fuelCurve.laden.referenceSpeed, "Laden reference speed"],
    [request.fuelCurve.laden.meConsumption, "Laden ME consumption"],
    [request.fuelCurve.laden.aeConsumption, "Laden AE consumption"],
    [request.fuelCurve.ballast.referenceSpeed, "Ballast reference speed"],
    [request.fuelCurve.ballast.meConsumption, "Ballast ME consumption"],
    [request.fuelCurve.ballast.aeConsumption, "Ballast AE consumption"],
  ] as const;

  draftFields.forEach(([value, label]) => {
    if (!isPositiveNumber(value)) errors.push(`${label} must be greater than zero.`);
  });

  if (request.performance.rows.length !== PERFORMANCE_KINDS.length) {
    errors.push("Performance must contain the four dashboard rows.");
  } else {
    request.performance.rows.forEach((row) => validatePerformanceRow(row, errors));
    const rowKinds = new Set(request.performance.rows.map((row) => row.kind));
    PERFORMANCE_KINDS.forEach((kind) => {
      if (!rowKinds.has(kind)) errors.push(`Performance row ${kind} is required.`);
    });
  }

  return errors;
};
